/** One tab of a note page. `count` is shown as "Label (n)" when it is a number. */
export interface NoteTab {
  id: string;
  label: string;
  count?: number | null;
}

/** Tab ids of the note pages (shared by View and Edit). */
export const NOTE_TAB_IDS = ['note', 'properties', 'attachments', 'related', 'comments', 'history'] as const;
export type NoteTabId = (typeof NOTE_TAB_IDS)[number];

export function isNoteTabId(value: unknown): value is NoteTabId {
  return typeof value === 'string' && (NOTE_TAB_IDS as readonly string[]).includes(value);
}

/** "Attachments (3)"; the bare label when there is no count. */
export function noteTabLabel(tab: NoteTab): string {
  return typeof tab.count === 'number' ? `${tab.label} (${tab.count})` : tab.label;
}

/** Navigation-state key carrying the open tab when switching between View and Edit of a note. */
export const NOTE_TAB_STATE_KEY = 'noteTab';

/**
 * The tab a note page opens on: the tab carried over from the other mode (View ⇄ Edit of the same
 * note, via navigation state), else a `?tab=` deep link, else the Note tab — so opening a note from
 * anywhere else (Home, links, …) always starts on Note.
 */
export function initialNoteTab(navigationState: unknown, queryTab: string | null): NoteTabId {
  const carried = (navigationState as Record<string, unknown> | null | undefined)?.[NOTE_TAB_STATE_KEY];
  if (isNoteTabId(carried)) return carried;
  if (isNoteTabId(queryTab)) return queryTab;
  return 'note';
}
